import { Button, Form, Input, message, Tooltip } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { QuestionCircleOutlined, SafetyOutlined } from "@ant-design/icons";
import { apiRequest } from "../../utils/apiClient";
import AuthBrandingPanel from "../../components/auth/AuthBrandingPanel";
import AuthLogo from "../../components/auth/AuthLogo";
import AuthMobileContact from "../../components/auth/AuthMobileContact";

const ResetPassword = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");

  useEffect(() => {
    const paramToken = searchParams.get("token");
    if (!paramToken) {
      message.error(t("tokenInvalidOrExpired"));
      navigate("/login");
      return;
    }
    setToken(paramToken);
  }, [searchParams, t, navigate]);

  const onFinish = async (values) => {
    if (!token) return;

    setLoading(true);
    try {
      await apiRequest(
        "/api/auth/reset-password",
        {
          method: "POST",
          body: JSON.stringify({
            token,
            password: values.password,
          }),
        },
        false
      );
      message.success(t("resetPasswordSuccess"));
      navigate("/login");
    } catch (error) {
      const errorMessage =
        error?.message ??
        error?.data?.message ??
        error?.data?.error ??
        t("somethingWentWrong");
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-x-hidden overflow-y-auto">
      <div className="flex justify-between h-full min-h-0">
        <div className="xl:w-2/6 w-full min-w-0 xl:min-w-[400px] xl:px-20 px-4 sm:px-10 pt-14 sm:pt-0 flex flex-col justify-center relative overflow-y-auto text-start">
          <AuthLogo />

          <h1 className="text-xl font-semibold tracking-tight text-gray-800 mb-1">
            {t("resetPassword")}
          </h1>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            {t("resetPasswordDescription")}
          </p>

          <Form layout="vertical" onFinish={onFinish} className="pb-20">
            <Form.Item
              label={
                <span>
                  {t("password")}{" "}
                  <Tooltip
                    overlayClassName="reset-password-tooltip"
                    title={
                      <div className="text-xs text-white space-y-1">
                        <div className="font-semibold text-white mb-2">
                          {t("passwordRulesTitle")}
                        </div>
                        <div className="text-white">• {t("passwordRuleLength")}</div>
                        <div className="text-white">• {t("passwordRuleUpper")}</div>
                        <div className="text-white">• {t("passwordRuleLower")}</div>
                        <div className="text-white">• {t("passwordRuleNum")}</div>
                        <div className="text-white">• {t("passwordRuleSpecial")}</div>
                      </div>
                    }
                  >
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="password"
              rules={[
                { required: true, message: t("pleaseEnterPassword") },
                { min: 8, message: t("passwordMinLength") },
                { pattern: /[A-Z]/, message: t("passwordUppercase") },
                { pattern: /[a-z]/, message: t("passwordLowercase") },
                { pattern: /[0-9]/, message: t("passwordNumber") },
                {
                  pattern: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
                  message: t("passwordSpecial"),
                },
              ]}
            >
              <Input.Password placeholder={t("enterStrongPassword")} size="large" />
            </Form.Item>

            <Form.Item
              label={t("confirmPassword")}
              name="passwordAgain"
              dependencies={["password"]}
              rules={[
                { required: true, message: t("pleaseConfirmPassword") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t("passwordsDoNotMatch"))
                    );
                  },
                }),
              ]}
            >
              <Input.Password placeholder={t("reEnterPassword")} size="large" />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                className="w-full font-medium"
                loading={loading}
                icon={<SafetyOutlined />}
              >
                {t("resetPassword")}
              </Button>
            </Form.Item>
          </Form>

          <div className="absolute bottom-6 left-0 w-full flex flex-col items-center justify-center text-center gap-1 px-4">
            <AuthMobileContact />
            <span className="text-gray-600">
              <Link to="/login" className="text-blue-600 inline-block p-2 font-medium hover:underline">
                {t("loginNow")}
              </Link>
            </span>
          </div>
        </div>

        <AuthBrandingPanel />
      </div>

      <style>{`
        .reset-password-tooltip .ant-tooltip-inner {
          color: #fff !important;
        }
        .reset-password-tooltip .ant-tooltip-arrow-content {
          background: rgba(0,0,0,.85) !important;
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;

